import type { Modality } from '@/features/quotes/context/group-life-quote.context';
import { useGroupLifeQuote } from '@/features/quotes/context/group-life-quote.context';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import {
  benefitKindFromDescription,
  GROUP_LIFE_MIN_INDIVIDUAL_CAPITAL,
  GROUP_LIFE_SALARY_MULTIPLIERS,
  isAcceptedSalaryMultiplier,
  salaryMultiplierLabel,
} from '@/features/quotes/data/group-life/capital-rules';
import { useBenefitTypes } from '@/features/quotes/hooks/common/use-benefit-types.hook';
import { useGroupLifeCoverageSchema } from '@/features/quotes/hooks/group-life/use-coverage-schema.hook';
import { useDailyBenefitMax } from '@/features/quotes/hooks/group-life/use-daily-benefit-max.hook';
import { useGroupLifeQuoteDetails } from '@/features/quotes/hooks/group-life/use-group-life-quote-details.hook';
import { useSaveGroupLifeStep } from '@/features/quotes/hooks/group-life/use-save-step.hook';
import type { CoverageDefinition } from '@/features/quotes/types/group-life/coverage-schema.types';
import { normalizeHydratedCoverageSelections } from '@/features/quotes/utils/group-life/coverage-hydration';
import {
  resolveAll,
  type CoverageSelection,
} from '@/features/quotes/utils/group-life/coverage-resolver';
import {
  formatCurrencyBRL,
  formatCurrencyInput,
  parseCurrencyInput,
} from '@/features/quotes/utils/group-life/format';
import { planHasPersistedCapitalFields } from '@/features/quotes/utils/group-life/plan-hydration';
import { useIsUnderwriterUser, useProductEmissionMode } from '@/shared/hooks';
import { toaster } from '@/shared/ui/toaster.component';
import {
  Button,
  Field,
  RadioCard,
  Select,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { useSearch } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Loader2,
  RefreshCcw,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { BenefitSelectionRow } from './step-plan/renderers/benefit-selection-row';
import { BenefitSelectionWithSiRow } from './step-plan/renderers/benefit-selection-with-si-row';
import { DeclaredRow } from './step-plan/renderers/declared-row';
import { ExclusiveGroupRow } from './step-plan/renderers/exclusive-group-row';
import { ProportionalToCoverageRow } from './step-plan/renderers/proportional-to-coverage-row';
import { ProportionalToRiskRow } from './step-plan/renderers/proportional-to-risk-row';

type Props = {
  onNext: () => void;
  onBack: () => void;
  isViewMode?: boolean;
};

const DEFAULT_DPS_WAIVER_CAPITAL = 'R$ 100.000,00';

function salaryMultiplierOption(salaries: string | number) {
  return { value: String(salaries), label: salaryMultiplierLabel(salaries) };
}

const SALARY_MULTIPLIER_OPTIONS = GROUP_LIFE_SALARY_MULTIPLIERS.map(
  salaryMultiplierOption
);

function getDpsWaiverCapital(value?: string): string {
  return (parseCurrencyInput(value ?? '') ?? 0) > 0
    ? value!
    : DEFAULT_DPS_WAIVER_CAPITAL;
}

function VariavelIcon({ className }: { className?: string }) {
  return (
    <svg
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
      aria-hidden='true'
    >
      <path
        d='M5 21V15M12 21V3M19 21V9'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}

function MultiploSalarialIcon({ className }: { className?: string }) {
  return (
    <svg
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
      aria-hidden='true'
    >
      <path
        d='M14.9992 9.00303L8.99922 15.003M8.99922 9.00303H9.00922M14.9992 15.003H15.0092'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}

function UniformeIcon({ className }: { className?: string }) {
  return (
    <svg
      width='20'
      height='20'
      viewBox='0 0 20 20'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
      aria-hidden='true'
    >
      <rect
        x='1'
        y='1'
        width='7'
        height='7'
        rx='1'
        stroke='currentColor'
        strokeWidth='2'
      />
      <rect
        x='12'
        y='1'
        width='7'
        height='7'
        rx='1'
        stroke='currentColor'
        strokeWidth='2'
      />
      <rect
        x='12'
        y='12'
        width='7'
        height='7'
        rx='1'
        stroke='currentColor'
        strokeWidth='2'
      />
      <rect
        x='1'
        y='12'
        width='7'
        height='7'
        rx='1'
        stroke='currentColor'
        strokeWidth='2'
      />
    </svg>
  );
}

/** Ícone por "kind" de modalidade (apresentação). A LISTA de modalidades vem
 *  sempre da DataTable `BenefitType`; aqui só associamos o ícone ao kind. */
const MODALITY_ICONS = {
  UNIFORM: UniformeIcon,
  VARIABLE: VariavelIcon,
  MULTIPLE_SALARY: MultiploSalarialIcon,
} as const;

/**
 * Uma cobertura é "básica" quando o grupo heurístico é BASIC OU quando a
 * categoria oficial (`CoverageCategory_CodeDesc` — a MESMA que a tela usa para
 * montar as seções) contém "básic". O `group` do back é inferido pelo nome do
 * container no product tree do InsureMO e nem sempre resolve `BASIC` (cai no
 * default `ADDITIONAL`); por isso a `category` é a fonte confiável.
 */
function isBasicCoverage(coverage: CoverageDefinition): boolean {
  if (coverage.group === 'BASIC') return true;
  const category = (coverage.category ?? '').toLowerCase();
  return category.includes('básic') || category.includes('basic');
}

export function StepPlan({
  onNext,
  onBack,
  isViewMode: isViewModeProp = false,
}: Props) {
  const {
    plan,
    setPlan,
    coverageSchema,
    setCoverageSchema,
    updateCoverage,
    stipulatorMembers,
    subStipulators,
  } = useGroupLifeQuote();
  const { productCode, policyId } = useQuoteParams();
  const search = useSearch({ strict: false }) as { quoteFlow?: string };
  const { isUnderwriterUser } = useIsUnderwriterUser();

  // Apólice clonada de PROPOSTA tem o plano inteiro bloqueado (modalidade,
  // capitais e coberturas). A API rejeita o save do step 'plan' nesses casos;
  // aqui é a defesa visual equivalente (trava tudo como no modo view).
  const detailsQuery = useGroupLifeQuoteDetails();
  const isProposalClone =
    detailsQuery.data?.clonedFrom?.[0]?.kind === 'PROPOSAL';
  const isViewMode = isViewModeProp || isProposalClone;

  // A lista fixa de múltiplos (10/12/15/20/24) só existe porque a emissão
  // automática traduz a quantidade para o `fatorMultiplo` do i4Pro. Produto em
  // emissão MANUAL não passa por esse de-para: a quantidade é digitada livre.
  const { isManualEmission } = useProductEmissionMode(
    detailsQuery.data?.productCode || productCode
  );

  const canConfigureDpsWaiverCapital =
    isUnderwriterUser === true && search.quoteFlow !== 'edit' && !isViewMode;

  const schemaQuery = useGroupLifeCoverageSchema({
    productCode: productCode || undefined,
  });

  // Modalidades vêm da DataTable `BenefitType` do InsureMO. Cada uma é
  // classificada por descrição no "kind" interno (UNIFORM/VARIABLE/
  // MULTIPLE_SALARY) que dirige os campos/validações. Daqui tiramos o label
  // exibido e o `code` inteiro persistido em `BenefitTypeCode`.
  const benefitTypesQuery = useBenefitTypes();
  const benefitByKind = useMemo(() => {
    const map: Partial<
      Record<Modality, { code: number; description: string }>
    > = {};
    for (const bt of benefitTypesQuery.data ?? []) {
      const kind = benefitKindFromDescription(bt.description);
      if (kind && !map[kind]) {
        map[kind] = { code: bt.code, description: bt.description };
      }
    }
    return map;
  }, [benefitTypesQuery.data]);

  // Modalidades exibidas vêm SEMPRE da DataTable `BenefitType` (sem hardcode):
  // cada linha vira um card. O "kind" (classificado pela descrição) define
  // ícone/campos/validações; `description` é o rótulo; `code` é o
  // `BenefitTypeCode` gravado no PolicyRiskGroup. Dedup por kind.
  const modalityOptions = useMemo(() => {
    const seen = new Set<Modality>();
    const opts: { kind: Modality; label: string }[] = [];
    for (const bt of benefitTypesQuery.data ?? []) {
      const kind = benefitKindFromDescription(bt.description);
      if (!kind || seen.has(kind)) continue;
      seen.add(kind);
      opts.push({ kind, label: bt.description });
    }
    return opts;
  }, [benefitTypesQuery.data]);

  // Sincroniza o schema com o contexto sempre que ele chegar / mudar.
  useEffect(() => {
    if (schemaQuery.data) setCoverageSchema(schemaQuery.data);
  }, [schemaQuery.data, setCoverageSchema]);

  const [modality, setModality] = useState<Modality>(
    plan?.modality ?? 'UNIFORM'
  );
  const [uniformCapital, setUniformCapital] = useState(
    plan?.uniformCapital ?? ''
  );
  const [numberOfSalaries, setNumberOfSalaries] = useState(
    plan?.numberOfSalaries ?? ''
  );
  const [minimumIndividualCapital, setMinimumIndividualCapital] = useState(
    plan?.minimumIndividualCapital ?? ''
  );
  const [maximumIndividualCapital, setMaximumIndividualCapital] = useState(
    plan?.maximumIndividualCapital ?? ''
  );
  const [maximumDpsWaiverCapital, setMaximumDpsWaiverCapital] = useState(
    getDpsWaiverCapital(plan?.maximumDpsWaiverCapital)
  );
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Re-sincroniza os campos locais quando o `plan` chega pela HIDRATAÇÃO (reabrir
  // cotação pelo grid → Editar). Os `useState` acima só leem `plan` no mount; se
  // a hidratação terminar depois que este componente montou, os campos ficariam
  // vazios para sempre (as coberturas já re-sincronizam por lerem `plan` direto).
  // Aplica uma única vez, no 1º plano HIDRATADO, para não sobrescrever edições.
  //
  // IMPORTANTE: só sincroniza quando o plano já carrega valores de capital/DPS
  // (plano salvo/hidratado). Selecionar uma cobertura cria um `plan` parcial via
  // `updateCoverage` (`{ modality:'UNIFORM', coverages:{} }`, sem capital/DPS) —
  // sincronizar a partir dele zeraria os campos que o usuário acabou de digitar.
  const planHydratedRef = useRef(false);
  useEffect(() => {
    planHydratedRef.current = false;
  }, [policyId]);

  useEffect(() => {
    if (planHydratedRef.current || !plan) return;
    if (!planHasPersistedCapitalFields(plan)) return;
    planHydratedRef.current = true;
    if (plan.modality) setModality(plan.modality);
    setUniformCapital(plan.uniformCapital ?? '');
    setNumberOfSalaries(plan.numberOfSalaries ?? '');
    setMinimumIndividualCapital(plan.minimumIndividualCapital ?? '');
    setMaximumIndividualCapital(plan.maximumIndividualCapital ?? '');
    setMaximumDpsWaiverCapital(
      getDpsWaiverCapital(plan.maximumDpsWaiverCapital)
    );
  }, [plan]);

  // Coberturas hidratadas sem `SumInsuredDefinition` chegam como DECLARED —
  // alinha com o schema (ex.: Morte/MA proporcionais ao risco).
  useEffect(() => {
    if (!coverageSchema || !plan?.coverages) return;
    const uniformCapitalReais =
      parseCurrencyInput(plan.uniformCapital ?? uniformCapital) ?? undefined;
    const normalized = normalizeHydratedCoverageSelections(
      coverageSchema,
      plan.coverages,
      { uniformCapitalReais }
    );
    if (normalized !== plan.coverages) {
      setPlan({ ...plan, coverages: normalized });
    }
  }, [coverageSchema, plan, setPlan, uniformCapital]);
  const { save, saving, error: saveError } = useSaveGroupLifeStep();

  // Soma do capital de todas as vidas — base para PROPORTIONAL_TO_RISK.
  const totalMemberCapital = useMemo(() => {
    const sumOfCapitals = (rows: { capital: string }[]) =>
      rows.reduce((acc, m) => acc + (parseCurrencyInput(m.capital) ?? 0), 0);
    return (
      sumOfCapitals(stipulatorMembers) +
      subStipulators.reduce((acc, s) => acc + sumOfCapitals(s.members), 0)
    );
  }, [stipulatorMembers, subStipulators]);

  /**
   * Base de risco (`MemberCapital`) para resolver o capital das coberturas
   * proporcionais.
   *
   * No step de Plano as vidas quase sempre ainda não existem, e `MemberCapital`
   * seria 0 — foi por isso que o valor embaixo do percentual saía zerado nas
   * modalidades Variável e Múltiplo Salarial. Sem vidas, quem responde pelo
   * risco é o capital do PLANO: o Capital Uniforme numa, o Capital Máximo
   * Individual nas outras duas.
   */
  const resolverMemberCapital = useMemo(() => {
    const planCapital =
      modality === 'UNIFORM'
        ? (parseCurrencyInput(uniformCapital) ?? 0)
        : (parseCurrencyInput(maximumIndividualCapital) ?? 0);

    return planCapital > 0
      ? Math.max(totalMemberCapital, planCapital)
      : totalMemberCapital;
  }, [modality, uniformCapital, maximumIndividualCapital, totalMemberCapital]);

  const selections = plan?.coverages ?? {};

  const dailyBenefitMaxByCoverage = useDailyBenefitMax({
    productCode: detailsQuery.data?.productCode || productCode || '930016',
    schema: coverageSchema,
    selections,
  });

  const resolved = useMemo(() => {
    if (!coverageSchema) return {};
    return resolveAll(coverageSchema, selections, {
      totalMemberCapital: resolverMemberCapital,
      dailyBenefitMaxByCoverage,
    });
  }, [
    coverageSchema,
    selections,
    resolverMemberCapital,
    dailyBenefitMaxByCoverage,
  ]);

  // Agrupa por categoria oficial (CoverageCategory_CodeDesc do InsureMO),
  // preservando a ordem de aparição. Coberturas sem categoria caem em
  // "Outras coberturas". Igual ao Figma (uma seção por categoria).
  const categorySections = useMemo(() => {
    const order: string[] = [];
    const byCategory = new Map<string, CoverageDefinition[]>();
    for (const c of coverageSchema?.coverages ?? []) {
      const cat = c.category?.trim() || 'Outras coberturas';
      if (!byCategory.has(cat)) {
        byCategory.set(cat, []);
        order.push(cat);
      }
      byCategory.get(cat)!.push(c);
    }
    return order.map(cat => ({ category: cat, items: byCategory.get(cat)! }));
  }, [coverageSchema]);

  const coverageNameByCode = useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of coverageSchema?.coverages ?? []) map[c.code] = c.name;
    return map;
  }, [coverageSchema]);

  const modalityErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    const min = parseCurrencyInput(minimumIndividualCapital);
    const max = parseCurrencyInput(maximumIndividualCapital);
    const floor = GROUP_LIFE_MIN_INDIVIDUAL_CAPITAL[modality];

    if (modality === 'UNIFORM') {
      // Capital Uniforme NÃO tem teto: acima de R$ 500.000 a cotação segue e a
      // regra 930016_UW_03_CapitalSegurado a encaminha para análise da
      // subscrição. Os 500k são gatilho de UW, não limite de contratação.
      if (!uniformCapital) errors.uniformCapital = 'Obrigatório';
    } else {
      // VARIABLE e MULTIPLE_SALARY: piso no mínimo. SEM teto, igual ao Capital
      // Uniforme acima — os R$ 500.000 são gatilho da regra
      // 930016_UW_03_CapitalSegurado, que encaminha a cotação para a análise da
      // subscrição, e não limite de contratação. Travar aqui impedia o corretor
      // de cotar um capital que o produto aceita.
      if (modality === 'MULTIPLE_SALARY') {
        if (!numberOfSalaries) errors.numberOfSalaries = 'Obrigatório';
        else if (isManualEmission) {
          if (!(Number(numberOfSalaries) > 0))
            errors.numberOfSalaries =
              'Informe uma quantidade de salários maior que zero.';
        }
        // Cotação antiga, de quando a quantidade era digitada livre.
        else if (!isAcceptedSalaryMultiplier(numberOfSalaries))
          errors.numberOfSalaries =
            'Selecione uma das quantidades de salários disponíveis.';
      }

      if (!minimumIndividualCapital)
        errors.minimumIndividualCapital = 'Obrigatório';
      else if (min != null && min < floor)
        errors.minimumIndividualCapital = `O Capital Mínimo Individual deve ser de no mínimo ${formatCurrencyBRL(floor)}.`;

      if (!maximumIndividualCapital)
        errors.maximumIndividualCapital = 'Obrigatório';
      else if (max != null && min != null && max < min)
        errors.maximumIndividualCapital =
          'O Capital Máximo Individual não pode ser menor que o Capital Mínimo Individual.';
    }

    // DPS (Capital Máximo de Dispensa) é opcional e SEM limite mínimo.
    return errors;
  }, [
    modality,
    uniformCapital,
    minimumIndividualCapital,
    maximumIndividualCapital,
    numberOfSalaries,
    isManualEmission,
  ]);

  const hasResolverErrors = Object.values(resolved).some(
    r => r.errors.length > 0
  );

  // Ao menos uma cobertura BÁSICA precisa estar selecionada (enabled) para
  // avançar. "Básica" é resolvida por `isBasicCoverage` (categoria oficial +
  // fallback no grupo). As demais (adicionais/serviços/dependentes) são
  // opcionais.
  const hasBasicCoverageSelected = useMemo(
    () =>
      (coverageSchema?.coverages ?? []).some(
        c => isBasicCoverage(c) && selections[c.code]?.enabled
      ),
    [coverageSchema, selections]
  );

  const handleSubmit = async () => {
    setSubmitAttempted(true);

    if (!hasBasicCoverageSelected) {
      toaster.create({
        type: 'warning',
        title: 'Selecione uma cobertura básica',
        description:
          'É obrigatório selecionar ao menos uma cobertura básica para avançar para a próxima etapa.',
      });
      return;
    }

    if (Object.keys(modalityErrors).length > 0 || hasResolverErrors || saving)
      return;

    setPlan({
      modality,
      uniformCapital: modality === 'UNIFORM' ? uniformCapital : undefined,
      numberOfSalaries:
        modality === 'MULTIPLE_SALARY' ? numberOfSalaries : undefined,
      minimumIndividualCapital:
        modality !== 'UNIFORM' ? minimumIndividualCapital : undefined,
      maximumIndividualCapital:
        modality !== 'UNIFORM' ? maximumIndividualCapital : undefined,
      maximumDpsWaiverCapital: canConfigureDpsWaiverCapital
        ? maximumDpsWaiverCapital || undefined
        : undefined,
      coverages: selections,
    });

    const coveragesPayload = (coverageSchema?.coverages ?? [])
      .map(def => {
        const sel = selections[def.code];
        if (!sel?.enabled) return null;
        const r = resolved[def.code];
        const isCheckboxOnlyAssistance =
          def.sumInsuredDefinition === 'DECLARED' &&
          def.hasSumInsured === false;
        const base = {
          CoverageCode: def.code,
          ProductElementId: def.productElementId,
          SumInsuredDefinition: def.sumInsuredDefinition,
          Capital: isCheckboxOnlyAssistance ? 0 : (r?.resolvedCapital ?? null),
        };
        switch (def.sumInsuredDefinition) {
          case 'PROPORTIONAL_TO_COVERAGE': {
            const proportion =
              sel.kind === 'PROPORTIONAL_TO_COVERAGE' &&
              sel.proportionOverride != null
                ? sel.proportionOverride
                : def.proportion;
            return {
              ...base,
              ReferenceCoverageCode: def.referenceCoverageCode,
              Proportion: proportion,
            };
          }
          case 'PROPORTIONAL_TO_RISK': {
            const proportion =
              sel.kind === 'PROPORTIONAL_TO_RISK' &&
              sel.proportionOverride != null
                ? sel.proportionOverride
                : def.proportion;
            return {
              ...base,
              RiskBasisCode: def.riskBasisCode,
              Proportion: proportion,
            };
          }
          case 'BENEFIT_SELECTION': {
            const childCode =
              sel.kind === 'BENEFIT_SELECTION'
                ? sel.selectedChildCoverageCode
                : null;
            const child = def.children.find(c => String(c.code) === childCode);
            return {
              ...base,
              // O select guarda o código da opção; o capital contratado é o
              // Sum Insured configurado para a opção selecionada no catálogo.
              Capital: child?.sumInsured ?? 0,
              SelectedChildCoverageCode: childCode,
              // ProductElementId do benefício → PolicyBenefitList no back.
              SelectedChildProductElementId: child?.productElementId,
            };
          }
          case 'BENEFIT_SELECTION_WITH_SI': {
            // `Capital` já vai no `base` (valor digitado = resolvedCapital).
            const childCode =
              sel.kind === 'BENEFIT_SELECTION_WITH_SI'
                ? sel.selectedChildCoverageCode
                : null;
            const child = def.children.find(c => String(c.code) === childCode);
            return {
              ...base,
              SelectedChildCoverageCode: childCode,
              SelectedChildProductElementId: child?.productElementId,
            };
          }
          case 'EXCLUSIVE_GROUP':
            return {
              ...base,
              ExclusionGroupCode: def.exclusionGroupCode,
            };
          default:
            return base;
        }
      })
      .filter(Boolean);

    const planPayload = {
      modality,
      // Código inteiro da modalidade (DataTable BenefitType) → BenefitTypeCode.
      benefitTypeCode: benefitByKind[modality]?.code,
      // UNIFORM → UniformCapital; VARIABLE/MULTIPLE → IndividualCapitalMin/Max;
      // MULTIPLE → + SalaryMultiplier. Gravados no PolicyRiskGroup pelo back.
      uniformCapital: modality === 'UNIFORM' ? uniformCapital : undefined,
      minIndividualCapital:
        modality !== 'UNIFORM' ? minimumIndividualCapital : undefined,
      maxIndividualCapital:
        modality !== 'UNIFORM' ? maximumIndividualCapital : undefined,
      salaryMultiplier:
        modality === 'MULTIPLE_SALARY' ? Number(numberOfSalaries) : undefined,
      dpsMaxCapital: maximumDpsWaiverCapital || undefined,
      coverages: coveragesPayload,
    };

    console.log('[group-life] plan step request body', {
      payload: planPayload,
    });

    const ok = await save('plan', planPayload);
    if (ok) onNext();
  };

  const showError = (key: string) => {
    // Capital Mínimo Individual e Dispensa de DPS dão feedback AO VIVO (conforme
    // o corretor digita): o erro de valor mínimo aparece assim que há um valor
    // digitado, sem esperar o submit. Os demais campos só validam no submit.
    if (key === 'minimumIndividualCapital') {
      return submitAttempted || minimumIndividualCapital
        ? modalityErrors[key]
        : undefined;
    }
    if (key === 'maximumIndividualCapital') {
      return submitAttempted || maximumIndividualCapital
        ? modalityErrors[key]
        : undefined;
    }
    return submitAttempted ? modalityErrors[key] : undefined;
  };

  // ─────── Estado de carregamento / erro do schema (bloqueante) ───────
  if (schemaQuery.isLoading) {
    return (
      <div className='flex items-center justify-center py-16'>
        <Loader2 className='w-6 h-6 animate-spin text-alba-violet-500' />
        <span className='ml-3 text-sm text-alba-gray-600'>
          Carregando catálogo de coberturas…
        </span>
      </div>
    );
  }

  if (schemaQuery.isError || !coverageSchema) {
    return (
      <div className='space-y-4'>
        <div className='rounded-lg border border-red-300 bg-red-50 p-4'>
          <div className='flex items-start gap-3'>
            <AlertTriangle className='w-5 h-5 text-red-600 shrink-0 mt-0.5' />
            <div className='flex-1'>
              <Text className='text-sm font-bold text-red-700'>
                Não foi possível carregar o catálogo de coberturas
              </Text>
              <Text className='mt-1 text-sm text-red-700'>
                {schemaQuery.error instanceof Error
                  ? schemaQuery.error.message
                  : 'Erro desconhecido ao consultar o produto técnico no InsureMO.'}
              </Text>
              <div className='mt-3 flex gap-2'>
                <Button
                  variant='secondary'
                  size='small'
                  onClick={() => void schemaQuery.refetch()}
                >
                  <RefreshCcw className='w-4 h-4' strokeWidth={1.8} />
                  Tentar novamente
                </Button>
                <Button variant='ghost' size='small' onClick={onBack}>
                  Voltar ao Step 1
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Cotação antiga com quantidade fora da lista: entra como opção só para ser
  // exibida — a validação exige trocar por uma das aceitas.
  const salaryMultiplierOptions =
    numberOfSalaries && !isAcceptedSalaryMultiplier(numberOfSalaries)
      ? [...SALARY_MULTIPLIER_OPTIONS, salaryMultiplierOption(numberOfSalaries)]
      : SALARY_MULTIPLIER_OPTIONS;

  return (
    <div className='space-y-6'>
      <Text className='text-lg font-bold text-alba-black-500'>Plano</Text>

      <Field.Root>
        <Field.Label>Modalidade</Field.Label>
        <RadioCard.Root
          value={modality}
          onValueChange={d => setModality(d.value as Modality)}
          disabled={isViewMode}
        >
          <div className='flex gap-2 flex-wrap'>
            {modalityOptions.map(opt => {
              const Icon = MODALITY_ICONS[opt.kind];
              return (
                <RadioCard.Item key={opt.kind} value={opt.kind}>
                  <RadioCard.ItemControl className='flex-row items-center gap-2'>
                    <Icon className='w-5 h-5' />
                    <RadioCard.ItemText>{opt.label}</RadioCard.ItemText>
                  </RadioCard.ItemControl>
                  <RadioCard.ItemHiddenInput />
                </RadioCard.Item>
              );
            })}
          </div>
        </RadioCard.Root>
      </Field.Root>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
        {modality === 'UNIFORM' && (
          <Field.Root invalid={!!showError('uniformCapital')}>
            <Field.Label>
              Capital Uniforme
              <span className='text-alba-error-red-500'>*</span>
            </Field.Label>
            <Field.Input
              value={uniformCapital}
              onChange={e =>
                setUniformCapital(formatCurrencyInput(e.target.value))
              }
              placeholder='R$ 0,00'
              disabled={isViewMode}
            />
            {showError('uniformCapital') && (
              <Field.ErrorText>{showError('uniformCapital')}</Field.ErrorText>
            )}
          </Field.Root>
        )}

        {modality === 'MULTIPLE_SALARY' && (
          <Field.Root invalid={!!showError('numberOfSalaries')}>
            <Field.Label>
              Quantidade de salários
              <span className='text-alba-error-red-500'>*</span>
            </Field.Label>
            {isManualEmission ? (
              <Field.Input
                value={numberOfSalaries}
                onChange={e =>
                  setNumberOfSalaries(e.target.value.replace(/\D/g, ''))
                }
                inputMode='numeric'
                maxLength={3}
                placeholder='Informe a quantidade'
                disabled={isViewMode}
              />
            ) : (
              <Select.Root
                value={numberOfSalaries ? [numberOfSalaries] : []}
                onValueChange={details =>
                  setNumberOfSalaries(details.value[0] ?? '')
                }
                positioning={{ sameWidth: true }}
                items={salaryMultiplierOptions}
                disabled={isViewMode}
              >
                <Select.Control>
                  <Select.Trigger className='min-w-0'>
                    <Select.ValueText
                      placeholder='Selecione o múltiplo'
                      className='min-w-0 truncate'
                    />
                    <Select.Indicator />
                  </Select.Trigger>
                </Select.Control>
                <Select.Positioner>
                  <Select.Content>
                    {salaryMultiplierOptions.map(item => (
                      <Select.Item key={item.value} item={item}>
                        <Select.ItemText>{item.label}</Select.ItemText>
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>
            )}
            {showError('numberOfSalaries') && (
              <Field.ErrorText>{showError('numberOfSalaries')}</Field.ErrorText>
            )}
          </Field.Root>
        )}

        {modality !== 'UNIFORM' && (
          <>
            <Field.Root invalid={!!showError('minimumIndividualCapital')}>
              <Field.Label>
                Capital Mínimo Individual
                <span className='text-alba-error-red-500'>*</span>
              </Field.Label>
              <Field.Input
                value={minimumIndividualCapital}
                onChange={e =>
                  setMinimumIndividualCapital(
                    formatCurrencyInput(e.target.value)
                  )
                }
                placeholder='R$ 0,00'
                disabled={isViewMode}
              />
              {showError('minimumIndividualCapital') && (
                <Field.ErrorText>
                  {showError('minimumIndividualCapital')}
                </Field.ErrorText>
              )}
            </Field.Root>
            <Field.Root invalid={!!showError('maximumIndividualCapital')}>
              <Field.Label>
                Capital Máximo Individual
                <span className='text-alba-error-red-500'>*</span>
              </Field.Label>
              <Field.Input
                value={maximumIndividualCapital}
                onChange={e =>
                  setMaximumIndividualCapital(
                    formatCurrencyInput(e.target.value)
                  )
                }
                placeholder='R$ 0,00'
                disabled={isViewMode}
              />
              {showError('maximumIndividualCapital') && (
                <Field.ErrorText>
                  {showError('maximumIndividualCapital')}
                </Field.ErrorText>
              )}
            </Field.Root>
          </>
        )}
      </div>

      {canConfigureDpsWaiverCapital && (
        <div className='border-t border-alba-gray-200 pt-4'>
          <Text className='font-bold text-alba-black-500 mb-2'>
            Declaração Pessoal de Saúde (DPS)
          </Text>
          <Field.Root invalid={!!showError('maximumDpsWaiverCapital')}>
            <Field.Label>Capital Máximo Dispensa</Field.Label>
            <Field.Input
              value={maximumDpsWaiverCapital}
              onChange={e =>
                setMaximumDpsWaiverCapital(formatCurrencyInput(e.target.value))
              }
              placeholder='R$ 0,00 (informativo)'
              disabled={isViewMode}
            />
            {showError('maximumDpsWaiverCapital') ? (
              <Field.ErrorText>
                {showError('maximumDpsWaiverCapital')}
              </Field.ErrorText>
            ) : (
              <Field.HelperText>
                Valor abaixo do qual DPS não é exigida (apenas informativo).
              </Field.HelperText>
            )}
          </Field.Root>
        </div>
      )}

      {/* Layout 2 colunas (igual ao Figma): categorias distribuídas
          sequencialmente — primeira metade na esquerda, resto na direita. */}
      <div className='border-t border-alba-gray-200 pt-4 grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4 items-start'>
        {[
          categorySections.slice(0, Math.ceil(categorySections.length / 2)),
          categorySections.slice(Math.ceil(categorySections.length / 2)),
        ].map((column, colIdx) => (
          <div key={colIdx} className='space-y-4'>
            {column.map(section => (
              <CoverageGroupSection
                key={section.category}
                label={section.category}
                items={section.items}
                selections={selections}
                resolved={resolved}
                coverageNames={coverageNameByCode}
                dailyBenefitMaxByCoverage={dailyBenefitMaxByCoverage}
                onUpdate={updateCoverage}
                disabled={isViewMode}
              />
            ))}
          </div>
        ))}
      </div>

      {saveError && (
        <div className='rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700'>
          {saveError}
        </div>
      )}
      {submitAttempted && hasResolverErrors && (
        <div className='rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700'>
          Existem coberturas com configuração inválida. Corrija os erros acima
          para avançar.
        </div>
      )}

      <div className='flex justify-between border-t border-alba-gray-200 pt-6'>
        <Button variant='ghost' size='large' onClick={onBack}>
          <ArrowLeft className='w-5 h-5' strokeWidth={1.6} />
          Voltar
        </Button>
        <Button
          variant='primary'
          size='large'
          onClick={handleSubmit}
          disabled={isViewMode || saving}
        >
          {saving ? 'Salvando…' : 'Próximo'}
          <ArrowRight className='w-5 h-5' strokeWidth={1.6} />
        </Button>
      </div>
    </div>
  );
}

function CoverageGroupSection({
  label,
  items,
  selections,
  resolved,
  coverageNames,
  dailyBenefitMaxByCoverage,
  onUpdate,
  disabled,
}: {
  label: string;
  items: CoverageDefinition[];
  selections: Record<string, CoverageSelection | undefined>;
  resolved: Record<
    string,
    { resolvedCapital: number | null; errors: string[] }
  >;
  coverageNames: Record<string, string>;
  dailyBenefitMaxByCoverage: Record<string, number>;
  onUpdate: (code: string, patch: CoverageSelection) => void;
  disabled?: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <div>
      {/* Mesmo componente de label dos campos "Modalidade"/"Capital Uniforme"
          (Field.Label do Alba DS) para manter a identidade visual. */}
      <Field.Root className='mb-2'>
        <Field.Label>{label}</Field.Label>
      </Field.Root>
      {/* "Switch Card" do Figma: border #B7BBBC, rounded-12, p-16, linhas
          separadas por divisória cinza. */}
      <div className='flex flex-col gap-4 rounded-[12px] border border-[#B7BBBC] bg-white p-4 [&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-alba-gray-200 [&>*:not(:last-child)]:pb-4'>
        {items.map(coverage => {
          const sel = selections[coverage.code];
          const r = resolved[coverage.code];
          const error = r?.errors[0];
          const isBasic = isBasicCoverage(coverage);
          const onChange = (next: CoverageSelection) =>
            onUpdate(coverage.code, next);
          switch (coverage.sumInsuredDefinition) {
            case 'DECLARED':
              return (
                <DeclaredRow
                  key={coverage.code}
                  coverage={coverage}
                  selection={sel}
                  onChange={onChange}
                  disabled={disabled}
                  error={error}
                />
              );
            case 'PROPORTIONAL_TO_COVERAGE':
              return (
                <ProportionalToCoverageRow
                  key={coverage.code}
                  coverage={coverage}
                  selection={sel}
                  resolvedCapital={r?.resolvedCapital ?? null}
                  referenceCoverageName={
                    coverageNames[coverage.referenceCoverageCode]
                  }
                  onChange={onChange}
                  disabled={disabled}
                  error={error}
                  isBasic={isBasic}
                />
              );
            case 'PROPORTIONAL_TO_RISK':
              return (
                <ProportionalToRiskRow
                  key={coverage.code}
                  coverage={coverage}
                  selection={sel}
                  resolvedCapital={r?.resolvedCapital ?? null}
                  onChange={onChange}
                  disabled={disabled}
                  error={error}
                  isBasic={isBasic}
                />
              );
            case 'BENEFIT_SELECTION':
              return (
                <BenefitSelectionRow
                  key={coverage.code}
                  coverage={coverage}
                  selection={sel}
                  onChange={onChange}
                  disabled={disabled}
                  error={error}
                />
              );
            case 'BENEFIT_SELECTION_WITH_SI':
              return (
                <BenefitSelectionWithSiRow
                  key={coverage.code}
                  coverage={coverage}
                  selection={sel}
                  onChange={onChange}
                  disabled={disabled}
                  error={error}
                  maxDailyRate={dailyBenefitMaxByCoverage[coverage.code]}
                />
              );
            case 'EXCLUSIVE_GROUP':
              return (
                <ExclusiveGroupRow
                  key={coverage.code}
                  coverage={coverage}
                  selection={sel}
                  onChange={onChange}
                  disabled={disabled}
                  error={error}
                />
              );
            default:
              return null;
          }
        })}
      </div>
    </div>
  );
}
